import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import api from "../../services/api";

const mergeById = (currentUsers, nextUsers) => {
  const usersMap = new Map();

  currentUsers.forEach(user => usersMap.set(user.id, user));
  nextUsers.forEach(user => usersMap.set(user.id, user));

  return Array.from(usersMap.values());
};

const usePaginatedUsers = ({ enabled = true, searchParam = "" } = {}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);

  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageNumberRef = useRef(0);

  const loadPage = useCallback(
    async ({ page = 1, reset = false } = {}) => {
      if (!enabled || loadingRef.current) return;
      if (!reset && !hasMoreRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        const { data } = await api.get("/users", {
          params: {
            searchParam,
            pageNumber: page
          }
        });

        const nextUsers = Array.isArray(data?.users) ? data.users : [];
        const nextHasMore = Boolean(data?.hasMore);

        setUsers(currentUsers =>
          reset ? nextUsers : mergeById(currentUsers, nextUsers)
        );
        setHasMore(nextHasMore);
        setPageNumber(page);
        hasMoreRef.current = nextHasMore;
        pageNumberRef.current = page;
      } catch (_error) {
        if (reset) {
          setUsers([]);
        }
        setHasMore(false);
        hasMoreRef.current = false;
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [enabled, searchParam]
  );

  useEffect(() => {
    if (!enabled) {
      setUsers([]);
      setHasMore(false);
      setPageNumber(0);
      hasMoreRef.current = false;
      pageNumberRef.current = 0;
      return;
    }

    setUsers([]);
    setHasMore(true);
    setPageNumber(0);
    hasMoreRef.current = true;
    pageNumberRef.current = 0;
    loadPage({ page: 1, reset: true });
  }, [enabled, loadPage]);

  const loadMore = useCallback(() => {
    loadPage({ page: pageNumberRef.current + 1 });
  }, [loadPage]);

  const handleScroll = useCallback(
    event => {
      const list = event.currentTarget;
      const reachedBottom =
        list.scrollTop + list.clientHeight >= list.scrollHeight - 24;

      if (reachedBottom) {
        loadMore();
      }
    },
    [loadMore]
  );

  const menuProps = useMemo(
    () => ({
      PaperProps: {
        style: { maxHeight: 320 },
        onScroll: handleScroll
      }
    }),
    [handleScroll]
  );

  const listboxProps = useMemo(
    () => ({
      onScroll: handleScroll
    }),
    [handleScroll]
  );

  return {
    users,
    loading,
    hasMore,
    pageNumber,
    loadMore,
    menuProps,
    listboxProps
  };
};

export default usePaginatedUsers;
